import { useDropdownKeyboardNavigation } from '@davidlloydleisure/common-js-frontend';
import classNames from 'classnames';
import type { CSSProperties, RefObject } from 'react';
import React, { use, useEffect, useId, useMemo, useRef, useState } from 'react';

import { ClubSearchLink } from 'src/components/common/club-search/club-search-link';
import { SiteContext } from 'src/context/site-context';
import { filterClubsBySearchKey, useClubSearchClubs } from 'src/custom-hooks/use-club-search-form';
import useOutsideClick from 'src/custom-hooks/use-outside-click';
import useWindowSize from 'src/custom-hooks/use-window-size';
import {
  amplitudeBrandName,
  amplitudeButtonTrackEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';
import { getClubLocations } from 'src/helpers/clubs-repository-helper';
import { getDistanceFromCurrentLocation, getUserLocation } from 'src/helpers/location-helper';
import type { ClubLocation } from 'src/models/club-location';

import Cross from '../../icons/cross';
import Map from '../../icons/map';
import Search from '../../icons/search-icon';

import * as styles from './club-search.module.scss';

type ClubSearchInputProps = {
  inputPlaceholder: string;
  pinnedSearchOptionText: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  isNavigation?: boolean;
  onFinish?: (event?: React.MouseEvent<HTMLElement>) => void;
  hideViewMap?: boolean;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  id?: string;
  inputName?: string;
  selectNearestClubTrigger?: number;
};

export const ClubSearchInput = ({
  inputPlaceholder,
  pinnedSearchOptionText,
  inputRef,
  isNavigation,
  onFinish,
  hideViewMap = false,
  className,
  inputClassName,
  dropdownClassName,
  id,
  inputName,
  selectNearestClubTrigger = 0,
}: ClubSearchInputProps) => {
  const containerRef = useRef(null);
  const { styling, pageLevel, buildType, language } = use(SiteContext);
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clubLocations, setClubLocations] = useState<Record<string, ClubLocation>>();
  const isClearingRef = useRef(false);
  const { isTabletOrSmaller } = useWindowSize();
  const allClubs = useClubSearchClubs();
  const filteredClubs = useMemo(
    () => filterClubsBySearchKey(searchValue, allClubs),
    [allClubs, searchValue]
  );
  const listboxId = useId();
  const viewMapButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | HTMLAnchorElement | null)[]>([]);
  const totalOptions = filteredClubs.length + 1; // +1 for view map button

  const { activeOptionIndex, hasActiveOption, handleKeyDown, resetActiveIndex, activateOption } =
    useDropdownKeyboardNavigation({
      isOpen: isDropdownOpen,
      optionCount: totalOptions,
      optionRefs,
      listRef: dropdownListRef,
      onOpen: () => setIsDropdownOpen(true),
      onClose: () => setIsDropdownOpen(false),
      onSelect: (index: number) => {
        optionRefs.current[index]?.click();
      },
    });
  const dropdownStyle = {
    '--club-search-dropdown-height': `${
      isDropdownOpen ? Math.min((filteredClubs.length + 1) * 3.75 + 1, 31) : 0
    }rem`,
  } as CSSProperties;

  useOutsideClick(containerRef, () => {
    if (!isTabletOrSmaller) {
      setIsDropdownOpen(false);
      resetActiveIndex();
    }
  });

  useEffect(() => {
    if (selectNearestClubTrigger === 0 || clubLocations) {
      return;
    }

    getClubLocations()
      .then(setClubLocations)
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(error);
      });
  }, [clubLocations, selectNearestClubTrigger]);

  useEffect(() => {
    if (selectNearestClubTrigger === 0 || !clubLocations) {
      return;
    }

    const selectNearestClub = async () => {
      const currentLocation = await getUserLocation();
      if (!currentLocation) {
        return;
      }

      const distanceSorter = (siteId: string) =>
        getDistanceFromCurrentLocation(siteId, clubLocations, currentLocation) ??
        Number.MAX_SAFE_INTEGER;
      const nearestClub = allClubs.toSorted(
        (club1, club2) => distanceSorter(club1.siteId) - distanceSorter(club2.siteId)
      )[0];

      if (!nearestClub) {
        return;
      }

      setSearchValue(nearestClub.clubName);
      setIsDropdownOpen(false);
      inputRef?.current?.focus();
    };

    void selectNearestClub();
  }, [allClubs, clubLocations, inputRef, selectNearestClubTrigger]);

  const handleClear = () => {
    isClearingRef.current = true;
    setIsDropdownOpen(false);
    setSearchValue('');
    resetActiveIndex();
    inputRef?.current?.focus();
  };

  const clearButton = (
    <button
      type="button"
      tabIndex={searchValue.length > 0 ? 0 : -1}
      onClick={handleClear}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClear();
        }
      }}
      aria-label="Clear search"
      className={classNames(styles.clearButton, searchValue.length > 0 && styles.shown)}
    >
      <Cross />
    </button>
  );

  const handleClickViewMap = () => {
    void amplitudeButtonTrackEvent({
      buildType,
      language,
      button: null,
      componentName: 'Club Search',
      pageLevel,
      ctaName: `View Map in the "Find a club" - ${isNavigation ? 'Navigation' : 'On The Page'}`,
    });
  };

  const handleInputFocus = () =>
    amplitudeTrackEvent(`Focus Input`, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: `Focus to Club List in the "Find a club" - ${isNavigation ? 'Navigation' : 'On The Page'}`,
      component: 'Club Search',
    });

  const viewMapLink = (
    <button
      ref={(el) => {
        viewMapButtonRef.current = el;
        optionRefs.current[0] = el;
      }}
      type="submit"
      role="option"
      id={`${listboxId}-option-0`}
      aria-selected={activeOptionIndex === 0}
      className={classNames(styles.viewMapButton, isDropdownOpen && styles.open)}
      onClick={handleClickViewMap}
      onMouseEnter={() => activateOption(0)}
    >
      <>
        <div className={styles.viewMapContent}>
          <Map />
          {pinnedSearchOptionText}
        </div>

        {!isTabletOrSmaller && (
          <span
            className={classNames(
              styles.viewMapDivider,
              filteredClubs.length > 0 && styles.visible
            )}
          />
        )}
      </>
    </button>
  );

  return (
    <div ref={containerRef} className={classNames(styles.inputContainer, className)}>
      <div className={styles.inputWrapper}>
        <div className={styles.searchIcon}>
          <Search fill={styling.colors.capeCod} />
        </div>
        <input
          id={id}
          ref={inputRef}
          value={searchValue}
          onChange={(e) => {
            const { value } = e.currentTarget;
            setIsDropdownOpen(value.trimStart().length > 0);
            setSearchValue(value.trimStart());
            resetActiveIndex();
          }}
          onFocus={() => {
            if (isClearingRef.current) {
              isClearingRef.current = false;
            } else {
              setIsDropdownOpen(
                (isAlreadyOpen) =>
                  isAlreadyOpen || searchValue.length > 0 || selectNearestClubTrigger === 0
              );
            }
            void handleInputFocus();
          }}
          onKeyDown={handleKeyDown}
          name={inputName ?? 'club-search'}
          autoComplete="off"
          autoCorrect="off"
          enterKeyHint="search"
          placeholder={inputPlaceholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isDropdownOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            hasActiveOption ? `${listboxId}-option-${activeOptionIndex}` : undefined
          }
          className={classNames(
            styles.input,
            isDropdownOpen &&
              (filteredClubs.length > 0 || hideViewMap === false) &&
              !isTabletOrSmaller &&
              styles.dropdownOpen,
            inputClassName
          )}
        />
        {clearButton}
      </div>
      <div
        ref={dropdownListRef}
        id={listboxId}
        role="listbox"
        className={classNames(
          styles.dropdown,
          isDropdownOpen && (filteredClubs.length > 0 || hideViewMap === false) && styles.open,
          dropdownClassName
        )}
        aria-hidden={!isDropdownOpen}
        style={dropdownStyle}
      >
        {hideViewMap === false && viewMapLink}
        {filteredClubs.map((club, index) => (
          <div
            key={`${club.siteId}-${searchValue}`}
            ref={(el) => {
              if (el) {
                optionRefs.current[index + 1] = el.querySelector('a');
              }
            }}
            className={styles.linkWrapper}
          >
            <ClubSearchLink
              club={club}
              isOpen={isDropdownOpen}
              onClick={(event) => {
                setIsDropdownOpen(false);
                onFinish?.(event);
              }}
              optionId={`${listboxId}-option-${index + 1}`}
              isActive={activeOptionIndex === index + 1}
              onMouseEnter={() => activateOption(index + 1)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
